import { useI18nSubscription } from '../../i18n/useI18nSubscription.js';
import { i18n } from '../../../i18n/index.js';
import { useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Badge, Button, Space, Tooltip, Typography } from 'antd';
import type { ColumnType } from 'antd/es/table/interface';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';

import { useCurrentScope } from '../../../shared/scope/useCurrentScope.js';
import { usePublishSelection } from '../../../shared/selection/useSelectionStore.js';
import { EntityHistoryButton } from '../../activity/components/EntityHistoryButton.js';
import { DeleteEntityButton } from '../../../shared/ui/DeleteEntityButton.js';
import { EntityTable } from '../../../shared/ui/EntityTable.js';
import { PageHeader } from '../../../shared/ui/PageHeader.js';
import { FilterBar, FilterSearch } from '../../../shared/ui/FilterBar.js';
import {
  ENTITY_NAME_LINK_STYLE,
  LIST_CREATE_BUTTON_STYLE,
  LIST_SURFACE_STYLE,
  listCountStyle,
} from '../../../shared/ui/list-styles.js';
import { QuietEmpty, QuietListSkeleton } from '../../../shared/ui/quiet-states.js';
import { GridColumnsButton } from '../../../shared/grid/GridColumnsButton.js';
import { useGridProfile, type GridColumnMeta } from '../../../shared/grid/useGridProfile.js';
import { useOrderedColumns } from '../../../shared/grid/useOrderedColumns.js';
import { byDate, byNumber, byText } from '../../../shared/ui/tableSorters.js';
import { formatDateTime } from '../../../shared/format/index.js';
import { ErrorState } from '../../../shared/ui/ErrorState.js';
import { CompanyFormModal } from '../components/CompanyFormModal.js';
import { useDeleteCompanyMutation } from '../hooks/useCompanyMutations.js';
import {
  getCompaniesQueryKey,
  getCompanyById,
  listCompanies,
  type CompanyDetail,
  type CompanyListItem,
} from '../api/companies.client.js';

const COMPANY_GRID_COLUMNS: GridColumnMeta[] = [
  {
    key: 'name',
    get label() {
      return i18n.t('crm.features.columns.company');
    },
  },
  {
    key: 'industry',
    get label() {
      return i18n.t('crm.features.columns.industry');
    },
  },
  {
    key: 'contacts',
    get label() {
      return i18n.t('crm.features.columns.contacts');
    },
  },
  {
    key: 'deals',
    get label() {
      return i18n.t('crm.features.columns.deals');
    },
  },
  {
    key: 'tasks',
    get label() {
      return i18n.t('crm.features.columns.tasks');
    },
  },
  {
    key: 'createdAt',
    get label() {
      return i18n.t('crm.features.columns.created_at');
    },
    hiddenByDefault: true,
  },
];

export default function CompaniesPage(): ReactElement {
  useI18nSubscription();
  const scope = useCurrentScope();
  const workspaceId = scope?.workspaceId ?? 'anonymous';
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [q, setQ] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState<CompanyDetail | null>(null);
  const deleteMutation = useDeleteCompanyMutation();

  const query = useQuery({
    queryKey: getCompaniesQueryKey(workspaceId),
    queryFn: () => listCompanies(),
    enabled: scope !== null,
  });

  // Поиск клиентский: список компаний сервер отдаёт целиком (без ограничения выборки).
  const companies = (query.data ?? []).filter((company) => {
    const needle = q.trim().toLowerCase();
    if (!needle) return true;
    return (
      company.name.toLowerCase().includes(needle) ||
      (company.industry ?? '').toLowerCase().includes(needle)
    );
  });
  // Публикуем выделение — быстрая заметка цепляется к отмеченным компаниям.
  usePublishSelection(
    'company',
    companies
      .filter((company) => selectedIds.includes(company.id))
      .map((company) => ({ id: company.id, label: company.name })),
  );

  // Список отдаёт краткую запись, а форме нужна полная — подтягиваем её по клику.
  const [editingId, setEditingId] = useState<string | null>(null);
  const openEdit = async (companyId: string): Promise<void> => {
    setEditingId(companyId);
    try {
      setEditingCompany(await getCompanyById(companyId));
    } finally {
      setEditingId(null);
    }
  };

  const columnMap: Record<string, ColumnType<CompanyListItem>> = {
    name: {
      title: i18n.t('crm.features.features.companies.pages.companiespage.компания'),
      dataIndex: 'name',
      key: 'name',
      sorter: byText<CompanyListItem>((row) => row.name),
      render: (_: unknown, company) => (
        <Link to={`/companies/${company.id}`} style={ENTITY_NAME_LINK_STYLE}>
          {company.name}
        </Link>
      ),
    },
    industry: {
      title: i18n.t('crm.features.features.companies.pages.companiespage.отрасль'),
      dataIndex: 'industry',
      key: 'industry',
      sorter: byText<CompanyListItem>((row) => row.industry),
      render: (value: string | null | undefined) =>
        value ?? <Typography.Text type="secondary">—</Typography.Text>,
    },
    contacts: {
      title: i18n.t('crm.features.features.companies.pages.companiespage.контакты'),
      key: 'contacts',
      width: 100,
      align: 'center',
      sorter: byNumber<CompanyListItem>((row) => row.contactsCount ?? 0),
      render: (_: unknown, company) => (
        <Badge
          count={company.contactsCount ?? 0}
          showZero
          style={listCountStyle(company.contactsCount ?? 0)}
        />
      ),
    },
    deals: {
      title: i18n.t('crm.features.features.companies.pages.companiespage.сделки'),
      key: 'deals',
      width: 100,
      align: 'center',
      sorter: byNumber<CompanyListItem>((row) => row.dealsCount ?? 0),
      render: (_: unknown, company) => (
        <Badge
          count={company.dealsCount ?? 0}
          showZero
          style={listCountStyle(company.dealsCount ?? 0)}
        />
      ),
    },
    tasks: {
      title: i18n.t('crm.features.features.companies.pages.companiespage.задачи'),
      key: 'tasks',
      width: 100,
      align: 'center',
      sorter: byNumber<CompanyListItem>((row) => row.tasksCount ?? 0),
      render: (_: unknown, company) => (
        <Badge
          count={company.tasksCount ?? 0}
          showZero
          style={listCountStyle(company.tasksCount ?? 0)}
        />
      ),
    },
    createdAt: {
      title: i18n.t('crm.features.features.companies.pages.companiespage.дата_создания'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      sorter: byDate<CompanyListItem>((row) => row.createdAt),
      render: (value: string) => (
        <Typography.Text type="secondary" style={{ fontSize: 13 }}>
          {formatDateTime(value)}
        </Typography.Text>
      ),
    },
  };

  const actionsColumn: ColumnType<CompanyListItem> = {
    title: i18n.t('crm.features.features.companies.pages.companiespage.действия'),
    key: 'actions',
    width: 110,
    align: 'center',
    render: (_: unknown, company) => (
      <Space size={4}>
        <Tooltip
          title={i18n.t('crm.features.features.companies.pages.companiespage.редактировать')}
        >
          <Button
            size="small"
            type="text"
            aria-label={i18n.t('crm.features.dynamic.features.companies.pages.companiespage.31', {
              p0: company.name,
            })}
            icon={<EditOutlined />}
            loading={editingId === company.id}
            onClick={() => void openEdit(company.id)}
          />
        </Tooltip>
        <EntityHistoryButton
          entity="company"
          entityId={company.id}
          title={i18n.t('crm.features.features.companies.pages.companiespage.история_компании')}
          subject={company.name}
          iconOnly
        />
        <DeleteEntityButton
          iconOnly
          label={i18n.t('crm.features.features.companies.pages.companiespage.удалить_компанию')}
          title={i18n.t('crm.features.features.companies.pages.companiespage.удалить_компанию_2')}
          description={i18n.t(
            'crm.features.features.companies.pages.companiespage.контакты_сделки_и_задачи_останутся_но_поте',
          )}
          loading={deleteMutation.isPending && deleteMutation.variables === company.id}
          onConfirm={() => deleteMutation.mutateAsync(company.id)}
        />
      </Space>
    ),
  };

  const grid = useGridProfile('companies', COMPANY_GRID_COLUMNS);
  const ordered = useOrderedColumns<CompanyListItem>({
    profile: grid,
    columnMap,
    trailing: [actionsColumn],
  });

  return (
    <div className="animate-fade-in flex h-full flex-col">
      <PageHeader
        title={i18n.t('crm.features.features.companies.pages.companiespage.компании')}
        subtitle={i18n.t(
          'crm.features.features.companies.pages.companiespage.компания_хаб_к_ней_привязываются_контакты_',
        )}
        actions={
          <Button
            type="primary"
            aria-label={i18n.t(
              'crm.features.features.companies.pages.companiespage.новая_компания',
            )}
            icon={<PlusOutlined />}
            style={LIST_CREATE_BUTTON_STYLE}
            onClick={() => setFormOpen(true)}
          >
            {i18n.t('crm.features.features.companies.pages.companiespage.новая_компания')}
          </Button>
        }
        testId="page-header"
      />

      <div className="flex min-h-0 flex-1 flex-col gap-3">
        <FilterBar
          search={
            <FilterSearch
              id="companies-search"
              aria-label={i18n.t('crm.features.companies_list.search_label')}
              placeholder={i18n.t(
                'crm.features.features.companies.pages.companiespage.поиск_по_названию_или_отрасли',
              )}
              value={q}
              onChange={setQ}
            />
          }
        >
          <GridColumnsButton profile={grid} />
        </FilterBar>

        <div
          className="min-h-0 flex-1 crm-checkbox-square"
          data-testid="companies-list-view"
          style={LIST_SURFACE_STYLE}
        >
          {query.isLoading ? (
            <QuietListSkeleton rows={6} />
          ) : query.isError ? (
            <div className="p-6">
              <ErrorState
                title={i18n.t(
                  'crm.features.features.companies.pages.companiespage.не_удалось_загрузить_компании',
                )}
                error={query.error}
                onRetry={() => void query.refetch()}
              />
            </div>
          ) : (
            <EntityTable<CompanyListItem>
              dataSource={companies}
              columns={ordered.columns}
              components={ordered.components}
              rowSelection={{
                columnWidth: 48,
                preserveSelectedRowKeys: true,
                selectedRowKeys: selectedIds,
                onChange: (keys) => setSelectedIds(keys.map(String)),
                getCheckboxProps: (company) => ({
                  title: i18n.t('crm.features.companies_list.select_row', { name: company.name }),
                  'aria-label': i18n.t('crm.features.companies_list.select_row', {
                    name: company.name,
                  }),
                }),
              }}
              locale={{
                emptyText: (
                  <QuietEmpty
                    text={i18n.t(
                      q.trim()
                        ? 'crm.features.companies_list.empty_filtered'
                        : 'crm.features.features.companies.pages.companiespage.компаний_пока_нет',
                    )}
                  />
                ),
              }}
              style={{ fontSize: 13 }}
            />
          )}
        </div>
      </div>

      <CompanyFormModal open={formOpen} onClose={() => setFormOpen(false)} />
      {editingCompany !== null ? (
        <CompanyFormModal open company={editingCompany} onClose={() => setEditingCompany(null)} />
      ) : null}
    </div>
  );
}
